import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getOrders } from '../../api/orders';
import { formatPnL } from '../../utils/formatters';
import PnLHeatmap from '../../components/PnLHeatmap/PnLHeatmap';   /* ← NEW */

/* ── inline styles only — no CSS module dependency ── */

const C = {
  green:  '#00e5a0',
  red:    '#ff4d6d',
  blue:   '#3d8ef0',
  amber:  '#f5a623',
  purple: '#a78bfa',
  bg:     '#060912',
  surface:'#0c1120',
  surface2:'#111827',
  border: '#1e2d45',
  border2:'#2a3f5f',
  muted:  '#5a6a82',
  muted2: '#8899b0',
};

const BROKER_COLOR = { dhan: C.amber, kite: C.blue };
const brokerColor  = (name = '') => BROKER_COLOR[name.toLowerCase()] ?? C.purple;

const mono = "'DM Mono', monospace";
const sans = "'Outfit', sans-serif";

/* ══════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════ */
function computeAnalytics(orders) {
  const closed = orders.filter(o => o.status === 'closed');
  const totalTrades = closed.length;
  const totalPnl    = closed.reduce((s, o) => s + (o.total_pnl ?? 0), 0);
  const winning     = closed.filter(o => (o.total_pnl ?? 0) > 0);
  const losing      = closed.filter(o => (o.total_pnl ?? 0) < 0);
  const winRate     = totalTrades > 0 ? (winning.length / totalTrades) * 100 : 0;
  const avgPnl      = totalTrades > 0 ? totalPnl / totalTrades : 0;
  const bestTrade   = closed.length ? Math.max(...closed.map(o => o.total_pnl ?? 0)) : 0;
  const worstTrade  = closed.length ? Math.min(...closed.map(o => o.total_pnl ?? 0)) : 0;

  /* partial exits */
  const withPartial  = closed.filter(o => !!o.addon_exit);
  const targetHit    = withPartial.filter(o => (o.squareoff_trigger ?? o.addon_exit?.trigger) === 'TARGET_HIT');
  const slHit        = withPartial.filter(o => (o.squareoff_trigger ?? o.addon_exit?.trigger) === 'SL_HIT');

  /* daily PnL */
  const byDate = {};
  closed.forEach(o => {
    const d = (o.created_at ?? '').slice(0, 10);
    if (!d) return;
    byDate[d] = byDate[d] ?? { pnl: 0, trades: 0 };
    byDate[d].pnl    += o.total_pnl ?? 0;
    byDate[d].trades += 1;
  });
  let cum = 0;
  const dailyPnl = Object.keys(byDate).sort().map(date => {
    cum += byDate[date].pnl;
    return { date, daily: Math.round(byDate[date].pnl), cumulative: Math.round(cum), trades: byDate[date].trades };
  });

  /* per instrument */
  const byInstr = {};
  closed.forEach(o => {
    const k = o.instrument ?? 'Unknown';
    byInstr[k] = byInstr[k] ?? { pnl: 0, trades: 0, won: 0 };
    byInstr[k].pnl    += o.total_pnl ?? 0;
    byInstr[k].trades += 1;
    if ((o.total_pnl ?? 0) > 0) byInstr[k].won++;
  });
  const perInstrument = Object.entries(byInstr)
    .map(([name, v]) => ({ name, pnl: Math.round(v.pnl), trades: v.trades, win_rate: v.trades > 0 ? (v.won / v.trades) * 100 : 0 }))
    .sort((a, b) => b.trades - a.trades);

  /* per broker */
  const byBroker = {};
  closed.forEach(o => {
    const k = o.broker_name ?? 'unknown';
    byBroker[k] = byBroker[k] ?? { pnl: 0, trades: 0, won: 0 };
    byBroker[k].pnl    += o.total_pnl ?? 0;
    byBroker[k].trades += 1;
    if ((o.total_pnl ?? 0) > 0) byBroker[k].won++;
  });
  const perBroker = Object.entries(byBroker).map(([name, v]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1), rawName: name,
    pnl: Math.round(v.pnl), trades: v.trades,
    win_rate: v.trades > 0 ? (v.won / v.trades) * 100 : 0,
  }));

  return {
    overall: { totalTrades, totalPnl, winRate, avgPnl, bestTrade, worstTrade, won: winning.length, lost: losing.length },
    dailyPnl, perInstrument, perBroker,
    openOrders: orders.filter(o => o.status === 'open').length,
    partialExits: { count: withPartial.length, targetHit: targetHit.length, slHit: slHit.length },
  };
}

/* ══════════════════════════════════════════════
   CUSTOM TOOLTIP
══════════════════════════════════════════════ */
function DarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', fontFamily: mono, fontSize: 12, minWidth: 160 }}>
      <div style={{ color: C.muted2, marginBottom: 6, fontSize: 11 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: p.color ?? '#fff', marginBottom: 2 }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>₹{Number(p.value).toLocaleString('en-IN')}</span>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════
   UI COMPONENTS
══════════════════════════════════════════════ */
function StatCard({ label, value, sub, accentColor, valColor }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 16, padding: '20px',
      position: 'relative', overflow: 'hidden',
      transition: 'border-color 0.2s',
      flex: 1, minWidth: 0,
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${accentColor}, transparent)` }} />
      <div style={{ fontSize: 10, fontWeight: 600, color: C.muted2, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 10, fontFamily: mono }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1, fontFamily: mono, color: valColor }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted2, marginTop: 6, fontFamily: mono }}>{sub}</div>}
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: 18 }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, color: '#fff', fontFamily: sans }}>{title}</div>
      <div style={{ padding: '20px 18px' }}>{children}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN ANALYTICS PAGE
══════════════════════════════════════════════ */
export default function Analytics() {
  const [orders,    setOrders]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const [filterBroker,     setFilterBroker]     = useState('');
  const [filterInstrument, setFilterInstrument] = useState('');
  const [filterFrom,       setFilterFrom]       = useState('');
  const [filterTo,         setFilterTo]         = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res     = await getOrders({ limit: 500 });
      const payload = res.data?.data ?? res.data;
      setOrders(Array.isArray(payload) ? payload : (payload?.orders ?? []));
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (!orders.length) return;
    let filtered = [...orders];
    if (filterBroker)     filtered = filtered.filter(o => o.broker_name?.toLowerCase() === filterBroker);
    if (filterInstrument) filtered = filtered.filter(o => o.instrument?.toLowerCase().includes(filterInstrument.toLowerCase()));
    if (filterFrom)       filtered = filtered.filter(o => (o.created_at ?? '') >= filterFrom);
    if (filterTo)         filtered = filtered.filter(o => (o.created_at ?? '') <= filterTo + ' 23:59:59');
    setAnalytics(computeAnalytics(filtered));
  }, [orders, filterBroker, filterInstrument, filterFrom, filterTo]);

  const brokerNames      = [...new Set(orders.map(o => o.broker_name).filter(Boolean))];
  const instrumentNames  = [...new Set(orders.map(o => o.instrument).filter(Boolean))].sort();
  const hasFilters       = filterBroker || filterInstrument || filterFrom || filterTo;
  const ov          = analytics?.overall ?? {};

  const inputStyle = {
    background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8,
    color: '#e8edf5', fontFamily: mono, fontSize: 12,
    padding: '7px 10px', outline: 'none', minWidth: 120,
  };

  const selectStyle = { ...inputStyle, cursor: 'pointer' };

  return (
    <div className="an-page" style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 28px 80px', fontFamily: sans }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          .an-two-col { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 768px) {
          .an-page { padding: 16px 16px 60px !important; }
          .an-stat-row > div { flex: 1 1 calc(50% - 7px) !important; min-width: calc(50% - 7px) !important; }
          .an-broker-chart { flex-direction: column !important; align-items: stretch !important; }
          .an-broker-chart > div:first-child { width: 100% !important; }
          .an-filter-bar { flex-direction: column !important; align-items: stretch !important; }
          .an-filter-item { width: 100% !important; }
          .an-filter-item select, .an-filter-item input { width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
        }
        @media (max-width: 480px) {
          .an-page { padding: 12px 12px 60px !important; }
          .an-stat-row > div { flex: 1 1 100% !important; min-width: 100% !important; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.muted, fontFamily: mono, marginBottom: 6 }}>
          <Link to="/dashboard" style={{ color: C.green, textDecoration: 'none' }}>Dashboard</Link>
          <span>›</span>
          <span>Analytics</span>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#ffffff', letterSpacing: '-0.5px', margin: 0 }}>Analytics</h1>
        <p style={{ fontSize: 11, color: C.muted2, marginTop: 4, fontFamily: mono }}>
          {loading ? 'Loading…' : `${orders.length} orders · closed trades only`}
        </p>
      </div>

      {/* Filters */}
      <div className="an-filter-bar" style={{
        display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap',
        marginBottom: 20, padding: '14px 16px',
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
      }}>
        {[
          { label: 'Broker', el: (
            <select style={selectStyle} value={filterBroker} onChange={e => setFilterBroker(e.target.value)}>
              <option value="">All brokers</option>
              {brokerNames.map(b => <option key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</option>)}
            </select>
          )},
          { label: 'Instrument', el: (
            <select style={selectStyle} value={filterInstrument} onChange={e => setFilterInstrument(e.target.value)}>
              <option value="">All instruments</option>
              {instrumentNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          )},
          { label: 'From', el: (
            <input style={inputStyle} type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
          )},
          { label: 'To', el: (
            <input style={inputStyle} type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
          )},
        ].map(({ label, el }) => (
          <div key={label} className="an-filter-item" style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: C.muted, fontFamily: mono }}>{label}</span>
            {el}
          </div>
        ))}
        {hasFilters && (
          <button
            onClick={() => { setFilterBroker(''); setFilterInstrument(''); setFilterFrom(''); setFilterTo(''); }}
            style={{ padding: '7px 14px', background: 'rgba(255,77,109,0.08)', border: '1px solid rgba(255,77,109,0.25)', borderRadius: 8, color: C.red, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: mono, alignSelf: 'flex-end' }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: 'rgba(255,77,109,0.08)', border: '1px solid rgba(255,77,109,0.25)', color: C.red, borderRadius: 8, padding: '10px 16px', fontSize: 13, marginBottom: 16, fontFamily: mono }}>
          ⚠ {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12, color: C.muted2, fontFamily: mono, fontSize: 13 }}>
          <div style={{ width: 28, height: 28, border: `2px solid ${C.border}`, borderTop: `2px solid ${C.green}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          Loading orders…
        </div>
      )}

      {!loading && analytics && (
        <>
          {/* Stat cards */}
          <div className="an-stat-row" style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
            <StatCard label="Total PnL"        value={formatPnL(ov.totalPnl ?? 0)} sub="All closed trades"    accentColor={C.green} valColor={(ov.totalPnl ?? 0) >= 0 ? C.green : C.red} />
            <StatCard label="Total Trades"     value={ov.totalTrades ?? 0}          sub={`${ov.won ?? 0} won · ${ov.lost ?? 0} lost`} accentColor={C.blue}  valColor="#ffffff" />
            <StatCard label="Win Rate"         value={`${(ov.winRate ?? 0).toFixed(1)}%`} sub="Closed trades only" accentColor={C.amber} valColor={(ov.winRate ?? 0) >= 50 ? C.green : C.red} />
            <StatCard label="Avg PnL / Trade"  value={formatPnL(ov.avgPnl ?? 0)}   sub={`Open: ${analytics.openOrders}`} accentColor={C.red} valColor={(ov.avgPnl ?? 0) >= 0 ? C.green : C.red} />
          </div>

          {/* Partial exits */}
          {analytics.partialExits.count > 0 && (
            <div style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
              padding: '16px 20px', marginBottom: 18,
            }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.muted2, letterSpacing: '1.2px', textTransform: 'uppercase', fontFamily: mono, marginBottom: 14 }}>
                Partial Exits
              </div>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: '#fff' }}>
                    {analytics.partialExits.count}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted2, fontFamily: mono, marginTop: 3 }}>
                    of {ov.totalTrades} trades had early partial exit
                  </div>
                </div>
                <div style={{ width: 1, height: 40, background: C.border, flexShrink: 0 }} />
                <div style={{ display: 'flex', gap: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                      background: 'rgba(0,229,160,0.08)', color: '#00e5a0',
                      border: '1px solid rgba(0,229,160,0.2)', fontFamily: mono,
                    }}>⊕ TARGET HIT</span>
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: mono, color: '#00e5a0' }}>
                      {analytics.partialExits.targetHit}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                      background: 'rgba(255,77,109,0.08)', color: '#ff4d6d',
                      border: '1px solid rgba(255,77,109,0.2)', fontFamily: mono,
                    }}>⊗ SL HIT</span>
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: mono, color: '#ff4d6d' }}>
                      {analytics.partialExits.slHit}
                    </span>
                  </div>
                  {analytics.partialExits.count - analytics.partialExits.targetHit - analytics.partialExits.slHit > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 5,
                        background: 'rgba(245,166,35,0.08)', color: '#f5a623',
                        border: '1px solid rgba(245,166,35,0.2)', fontFamily: mono,
                      }}>⊟ OTHER</span>
                      <span style={{ fontSize: 16, fontWeight: 700, fontFamily: mono, color: '#f5a623' }}>
                        {analytics.partialExits.count - analytics.partialExits.targetHit - analytics.partialExits.slHit}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══ P&L HEATMAP ══ */}
          {analytics.dailyPnl.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <PnLHeatmap
                dailyPnl={analytics.dailyPnl}
                title="P&L Calendar"
                months={6}
              />
            </div>
          )}

          {/* Best / Worst */}
          <div className="an-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
            <div style={{ background: 'rgba(0,229,160,0.04)', border: '1px solid rgba(0,229,160,0.15)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: mono, marginBottom: 5 }}>Best Trade</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: C.green }}>{formatPnL(ov.bestTrade ?? 0)}</div>
              </div>
              <span style={{ fontSize: 28, opacity: 0.3 }}>🏆</span>
            </div>
            <div style={{ background: 'rgba(255,77,109,0.04)', border: '1px solid rgba(255,77,109,0.15)', borderRadius: 12, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '1px', fontFamily: mono, marginBottom: 5 }}>Worst Trade</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: C.red }}>{formatPnL(ov.worstTrade ?? 0)}</div>
              </div>
              <span style={{ fontSize: 28, opacity: 0.3 }}>📉</span>
            </div>
          </div>

          {/* Daily PnL chart */}
          {analytics.dailyPnl.length > 0 && (
            <Block title="Daily PnL Trend">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={analytics.dailyPnl} margin={{ top: 5, right: 16, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 10, fontFamily: mono }} tickLine={false} axisLine={{ stroke: C.border }} />
                  <YAxis tick={{ fill: C.muted, fontSize: 10, fontFamily: mono }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<DarkTooltip />} />
                  <Legend wrapperStyle={{ fontFamily: mono, fontSize: 11, color: C.muted2 }} />
                  <Line type="monotone" dataKey="cumulative" name="Cumulative PnL" stroke={C.green} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: C.green }} />
                  <Line type="monotone" dataKey="daily" name="Daily PnL" stroke={C.blue} strokeWidth={1.5} dot={false} strokeDasharray="4 2" activeDot={{ r: 4, fill: C.blue }} />
                </LineChart>
              </ResponsiveContainer>
            </Block>
          )}

          {/* Instrument + Broker */}
          <div className="an-two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>
            {analytics.perInstrument.length > 0 && (
              <Block title="PnL by Instrument">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analytics.perInstrument} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                    <XAxis dataKey="name" tick={{ fill: C.muted2, fontSize: 11, fontFamily: mono }} tickLine={false} axisLine={{ stroke: C.border }} />
                    <YAxis tick={{ fill: C.muted, fontSize: 10, fontFamily: mono }} tickLine={false} axisLine={false} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<DarkTooltip />} />
                    <Bar dataKey="pnl" name="PnL" radius={[4,4,0,0]}>
                      {analytics.perInstrument.map((e, i) => <Cell key={i} fill={e.pnl >= 0 ? C.green : C.red} fillOpacity={0.85} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Block>
            )}

            {analytics.perBroker.length > 0 && (
              <Block title="Trades by Broker">
                <div className="an-broker-chart" style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <ResponsiveContainer width={160} height={160}>
                    <PieChart>
                      <Pie data={analytics.perBroker} dataKey="trades" nameKey="name" cx="50%" cy="50%" innerRadius={44} outerRadius={72} paddingAngle={3}>
                        {analytics.perBroker.map((e, i) => <Cell key={i} fill={brokerColor(e.rawName)} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, `${n} Trades`]} contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, fontFamily: mono, fontSize: 12 }} itemStyle={{ color: '#e8edf5' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ flex: 1 }}>
                    {analytics.perBroker.map((d, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: brokerColor(d.rawName), flexShrink: 0, display: 'inline-block' }} />
                          <span style={{ fontSize: 12, color: '#e8edf5', fontFamily: mono }}>{d.name}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: d.pnl >= 0 ? C.green : C.red, fontFamily: mono }}>{formatPnL(d.pnl)}</div>
                          <div style={{ fontSize: 10, color: C.muted2, fontFamily: mono }}>{d.trades} trades</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Block>
            )}
          </div>

          {/* Instrument table */}
          {analytics.perInstrument.length > 0 && (
            <Block title="Instrument Breakdown">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Instrument', 'Trades', 'Win Rate', 'Total PnL'].map(c => (
                      <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: C.muted2, letterSpacing: '1px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}`, fontFamily: mono }}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analytics.perInstrument.map((row, i) => (
                    <tr key={i} style={{ borderBottom: i < analytics.perInstrument.length - 1 ? `1px solid rgba(30,45,69,0.5)` : 'none' }}>
                      <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: '#ffffff', fontFamily: sans }}>{row.name}</td>
                      <td style={{ padding: '12px 14px', fontSize: 12.5, color: '#ffffff', fontFamily: mono, fontVariantNumeric: 'tabular-nums' }}>{row.trades}</td>
                      <td style={{ padding: '12px 14px', fontSize: 12.5, fontFamily: mono, fontWeight: 600, color: row.win_rate >= 50 ? C.green : C.red }}>{row.win_rate.toFixed(1)}%</td>
                      <td style={{ padding: '12px 14px', fontSize: 12.5, fontFamily: mono, fontWeight: 600, color: row.pnl >= 0 ? C.green : C.red, fontVariantNumeric: 'tabular-nums' }}>{formatPnL(row.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Block>
          )}

          {ov.totalTrades === 0 && (
            <div style={{ textAlign: 'center', color: C.muted2, fontFamily: mono, fontSize: 13, padding: '60px 0' }}>
              No closed orders found for the selected filters
            </div>
          )}
        </>
      )}
    </div>
  );
}